import type {
    ChangePasswordRequestBody,
    CreateSystemUserRequestBody,
    CreateSystemUserResponse,
    GetSystemUserListRequestQuery,
    GetSystemUserListResponse,
    GetSystemUserResponse,
    GetSystemUserRoleListResponse,
    SystemUser,
    UpdateSystemUserRequestBody,
} from '@engagement-web-app/common';
import { APIError } from './types';

export async function getCurrentSystemUser(): Promise<GetSystemUserResponse> {
    try {
        const res = await fetch('/api/system/users/me');
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchSystemUsers({
    from,
    limit,
    search,
}: GetSystemUserListRequestQuery): Promise<GetSystemUserListResponse> {
    try {
        const params = new URLSearchParams();
        if (from !== undefined) params.set('from', String(from));
        if (limit !== undefined) params.set('limit', String(limit));
        if (search) params.set('search', search);
        const res = await fetch(`/api/system/users?${params.toString()}`);
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function createSystemUser(body: CreateSystemUserRequestBody): Promise<CreateSystemUserResponse> {
    try {
        const res = await fetch('/api/system/users', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function updateSystemUser(id: string, body: UpdateSystemUserRequestBody): Promise<SystemUser> {
    try {
        const res = await fetch(`/api/system/users/${id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function deleteSystemUser(uuid: string): Promise<void> {
    try {
        const res = await fetch(`/api/system/users/${uuid}`, {
            method: 'DELETE',
        });
        if (!res.ok) {
            const data = await res.json();
            throw APIError.fromResponse(data);
        }
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function resetSystemUserPassword(uuid: string): Promise<void> {
    try {
        const res = await fetch(`/api/system/users/${uuid}/password-reset`, {
            method: 'POST',
        });
        if (!res.ok) {
            const data = await res.json();
            throw APIError.fromResponse(data);
        }
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function resetSystemUserMfa(uuid: string): Promise<void> {
    try {
        const res = await fetch(`/api/system/users/${uuid}/mfa-reset`, {
            method: 'POST',
        });
        if (!res.ok) {
            const data = await res.json();
            throw APIError.fromResponse(data);
        }
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function requestMfaReset(): Promise<void> {
    try {
        const res = await fetch('/api/system/users/me/mfa-reset', {
            method: 'POST',
        });
        if (!res.ok) {
            const data = await res.json();
            throw APIError.fromResponse(data);
        }
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchSystemUserRoles(): Promise<GetSystemUserRoleListResponse> {
    try {
        const res = await fetch('/api/system/user-roles');
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function changePassword(body: ChangePasswordRequestBody): Promise<void> {
    try {
        const res = await fetch('/api/system/users/me/password', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        if (!res.ok) {
            const data = await res.json();
            throw APIError.fromResponse(data);
        }
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function requestPasswordReset(): Promise<void> {
    try {
        const res = await fetch('/api/system/users/me/password-reset', {
            method: 'POST',
        });
        if (!res.ok) {
            const data = await res.json();
            throw APIError.fromResponse(data);
        }
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}
